/**
 * Parses `{ status_code, data: T[] }` list responses from the backend service.
 * @module unwrapBackendListData
 */

/** Standard backend list envelope for GET list/search endpoints. */
export interface BackendListEnvelope<T> {
  /** Application status mirrored from HTTP (e.g. 200). */
  status_code: number;
  /** List payload. */
  data: T[];
}

/**
 * Extracts the list array from a backend `{ status_code, data }` envelope.
 *
 * @param body - Parsed JSON from a successful response.
 * @param httpStatus - HTTP status from the `Response` (compared to `status_code` when present).
 * @param label - Short name for error messages (e.g. "OncoEMR tasks").
 * @return The `data` array from the envelope.
 * @throws Error when the body is not a valid envelope or `status_code` disagrees with HTTP status.
 */
export const unwrapBackendListData = <T>(
  body: unknown,
  httpStatus: number,
  label: string,
): T[] => {
  if (
    body === null ||
    typeof body !== "object" ||
    !("data" in body) ||
    !Array.isArray((body as BackendListEnvelope<T>).data)
  ) {
    throw new Error(
      `${label} response is not a list envelope with a data array`,
    );
  }

  const envelope = body as BackendListEnvelope<T>;

  if (
    typeof envelope.status_code === "number" &&
    envelope.status_code !== httpStatus
  ) {
    throw new Error(
      `${label} response status_code (${envelope.status_code}) does not match HTTP status (${httpStatus})`,
    );
  }

  return envelope.data;
};
